import React, { useEffect, useState } from "react";
import { Avatar, Button, Col, Input, Modal, Row, message, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import checkedSign from "../../../Assets/susbcription_mangement/checkIcon.svg";
import API_MANAGER from "../../../API";

const TransferUserModal = ({
  isModalOpen,
  handleCancel,
  handleOk,
  candidateSelectedData,
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [userData, setUserData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const getUserData = async () => {
    setLoading(true);
    const param = {
      search: search ? search : "",
      type: "NEWS",
    };
    try {
      const data = await API_MANAGER.getRecruiterUserList(param);
      const users = (data?.data?.data || []).filter(
        (item) => item?.user?.isActive !== false
      );
      setUserData(users);
    } catch (error) {
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      getUserData();
      setSelectedUsers([]);
      setRecipientEmails([]);
      setEmailInput("");
      setSearch("");
    }
  }, [isModalOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isModalOpen) {
        getUserData();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const handleRowSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) {
      message.warning("Please enter an email address.");
      return;
    }
    if (!validateEmail(email)) {
      message.error("Please enter a valid email address.");
      return;
    }
    if (recipientEmails.includes(email)) {
      message.warning("This email is already added.");
      return;
    }
    // Check if email exists in userData
    const emailExists = userData.some(
      (user) => user?.user?.email?.toLowerCase() === email.toLowerCase()
    );
    if (emailExists) {
      message.warning("This email belongs to a user in the list. Please select the user instead.");
      return;
    }
    setRecipientEmails([...recipientEmails, email]);
    setEmailInput("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setRecipientEmails(recipientEmails.filter((email) => email !== emailToRemove));
  };

  const handleShare = () => {
    if (selectedUsers.length === 0 && recipientEmails.length === 0) {
      message.warning("Please select at least one user or add an email.");
      return;
    }
    handleOk({
      recipientUserIds: selectedUsers,
      recipientEmails: recipientEmails,
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0]?.toUpperCase() || "";
    const last = lastName?.[0]?.toUpperCase() || "";
    return first + last;
  };

  return (
    <Modal
      centered
      getContainer={document.body}
      title="Users"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={[
        <Row key="footer">
          <Col span={24} align={"right"} className="justify_items_end">
            <Button key="cancel" onClick={handleCancel} className="cancel_btn">
              Cancel
            </Button>
            <Button
              key="share"
              type="primary"
              onClick={handleShare}
              className="black_color_button"
            >
              Share
            </Button>
            ,
          </Col>
        </Row>,
      ]}
      wrapClassName="newsModalSearch"
      width={520}
    >
      <Input
        className="modal-search mb-20"
        size="large"
        placeholder="Search user"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        prefix={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
          >
            <path
              d="M19.8947 19.0002L15.5517 14.6572M15.5517 14.6572C16.2946 13.9143 16.8839 13.0324 17.286 12.0618C17.688 11.0911 17.8949 10.0508 17.8949 9.00021C17.8949 7.9496 17.688 6.90929 17.286 5.93866C16.8839 4.96803 16.2946 4.08609 15.5517 3.34321C14.8088 2.60032 13.9269 2.01103 12.9563 1.60898C11.9857 1.20693 10.9453 1 9.89474 1C8.84414 1 7.80382 1.20693 6.83319 1.60898C5.86256 2.01103 4.98062 2.60032 4.23774 3.34321C2.73741 4.84354 1.89453 6.87842 1.89453 9.00021C1.89453 11.122 2.73741 13.1569 4.23774 14.6572C5.73807 16.1575 7.77295 17.0004 9.89474 17.0004C12.0165 17.0004 14.0514 16.1575 15.5517 14.6572Z"
              stroke="#1638B1"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
      />
      
      {/* Add Email Section */}
      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Input
            placeholder="Add email address"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onPressEnter={handleAddEmail}
            size="large"
          />
        </Col>
        <Col>
          <Button type="primary" onClick={handleAddEmail} size="large">
            Add
          </Button>
        </Col>
      </Row>

      {/* Display Added Emails */}
      {recipientEmails.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontSize: 12, color: "#666", fontWeight: "bold" }}>
            Added Emails:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {recipientEmails.map((email, index) => (
              <Tag
                key={index}
                closable
                onClose={() => handleRemoveEmail(email)}
                className="email-tag-pill"
                style={{ 
                  marginBottom: 4,
                  backgroundColor: "#091647",
                  color: "#ffffff",
                  borderColor: "#091647",
                  padding: "4px 12px",
                  fontSize: "14px",
                  height: "auto",
                  lineHeight: "1.5"
                }}
              >
                {email}
              </Tag>
            ))}
          </div>
        </div>
      )}
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading...
          </div>
        ) : userData?.length > 0 ? (
          <Row>
            <Col span={24} className="modal-card-parent">
              {userData?.map((e, ind) => (
                <Row
                  key={e?.user?.id}
                  justify={"space-between"}
                  align={"middle"}
                  className="modal-cards cursor_pointer"
                  style={{
                    background: selectedUsers.includes(e?.user?.id)
                      ? "#e8eaec"
                      : "#ffffff",
                    borderBottom: "1px solid #E4E8ED",
                    padding: "12px 0",
                  }}
                  onClick={() => handleRowSelect(e?.user?.id)}
                >
                  <Col xs={3} md={2}>
                    <Avatar
                      style={{
                        backgroundColor: "#f56a00",
                        verticalAlign: "middle",
                      }}
                    >
                      {getInitials(e?.user?.firstName, e?.user?.lastName)}
                    </Avatar>
                  </Col>

                  <Col xs={18} md={19} className="modal-text">
                    {e?.user?.firstName}&nbsp;{e?.user?.lastName}
                  </Col>
                  <Col xs={2} md={2}>
                    {selectedUsers.includes(e?.user?.id) ? (
                      <img src={checkedSign} alt="checked" />
                    ) : (
                      ""
                    )}
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
            No users found
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TransferUserModal;

